<script setup lang="ts">
import { type Ref, ref, watch, computed } from "vue";
import { useJourneyStore } from "@/stores/journey-store";
import type { RailCardItem } from "@/models/remote/RailCardsResponse";
import { getRailcards } from "@/graphql/functions/getRailcards";

const store = useJourneyStore();
const railcards: Ref<Array<RailCardItem>> = ref<Array<RailCardItem>>([]);
const isApiLoading = ref(false);

function loadRailcards() {
    return new Promise(async (resolve, reject) => {
        try {
            isApiLoading.value = true;

            const result = await getRailcards();

            railcards.value = result;
            isApiLoading.value = false;

            resolve(null);
            return;
        } catch (error) {
            console.error(error);
            isApiLoading.value = false;
            reject();
            return;
        }
    });
}

// Returns an array of numbers to be used in the dropdown, based on how many total passengers there are
function getArrayOfItemsForDropdown() {
    return Array.from({ length: maxNumberOfRailcards.value }, (_, i) => i + 1);
}

// Returns the maximum number of railcards that can be selected based on the number of adults and children
const maxNumberOfRailcards = computed(() => {
    return store.query.adults + store.query.children;
});

function handleRailcardUpdate(railcard: RailCardItem) {
    store.query.railCard = railcard;
    if (railcard === null) {
        store.query.railCardQuantity = 0;
    } else if (store.query.railCardQuantity === 0) {
        store.query.railCardQuantity = 1;
    }
}

function handleRailcardQuantityUpdate(quantity: number) {
    store.query.railCardQuantity = quantity;
}

// Update the number of railcards if there are now less passengers than railcards selected
function updateRailcardQuantityBasedOnPassengerCount() {
    if (store.query.railCardQuantity > maxNumberOfRailcards.value) {
        store.query.railCardQuantity = maxNumberOfRailcards.value;
    }
}

watch(
    () => store.query.adults,
    () => {
        updateRailcardQuantityBasedOnPassengerCount();
    }
);
watch(
    () => store.query.children,
    () => {
        updateRailcardQuantityBasedOnPassengerCount();
    }
);

loadRailcards();
</script>

<template>
    <div class="passengers-railcards-wrapper">
        <v-select @update:model-value="handleRailcardUpdate" :model-value="store.query.railCard" density="compact" class="railcard-select" return-object clearable :items="railcards" item-title="name" variant="outlined" hide-details label="Railcard"> </v-select>

        <v-select class="railcard-quantity-select" @update:model-value="handleRailcardQuantityUpdate" :model-value="store.query.railCardQuantity" :items="getArrayOfItemsForDropdown()" hide-details variant="outlined" label="Quantity" density="compact"></v-select>
    </div>
</template>

<style scoped lang="scss">
.passengers-railcards-wrapper {
    gap: 8px;
    display: grid;
    grid-template-columns: 1fr max-content;
    width: 100%;
}

.railcard-quantity-select {
    min-width: 100px;
}
@media (max-width: 400px) {
    .passengers-railcards-wrapper {
        grid-template-columns: 100%;
    }
    .passengers-railcards-wrapper .railcard-select {
        grid-row: 1/1;
        grid-column: 1/1;
    }
    .passengers-railcards-wrapper .railcard-quantity-select {
        grid-row: 2/2;
        grid-column: 1/1;
        margin-top: 8px;
    }
}
</style>
